import React, { PropsWithChildren, useCallback, useLayoutEffect, useRef, useState } from 'react';

import type { ImageProps } from 'expo-image';
import { ImageStyle, LayoutChangeEvent, StyleProp, StyleSheet, View } from 'react-native';

import { FullWidthAutoHeightImage } from 'components/ui/full-width-auto-height-image';

interface PdpSummaryHeroProps extends PropsWithChildren {
  backgroundImage: ImageProps['source'];
  backgroundImageStyle?: StyleProp<ImageStyle>;
}

export const PdpSummaryHero = ({
  backgroundImage,
  backgroundImageStyle,
  children,
}: PdpSummaryHeroProps) => {
  const contentRef = useRef<View>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const updateContentHeight = useCallback((nextContentHeight: number) => {
    setContentHeight(currentHeight =>
      currentHeight === nextContentHeight ? currentHeight : nextContentHeight
    );
  }, []);

  useLayoutEffect(() => {
    const contentRect = contentRef.current?.getBoundingClientRect?.();
    if (contentRect) {
      updateContentHeight(contentRect.height);
    }
  }, [updateContentHeight]);

  const handleContentLayout = useCallback(
    ({ nativeEvent }: LayoutChangeEvent) => updateContentHeight(nativeEvent.layout.height),
    [updateContentHeight]
  );

  return (
    <View style={[styles.container, { minHeight: contentHeight }]} testID="pdp-summary-hero">
      <FullWidthAutoHeightImage
        accessible={false}
        pointerEvents="none"
        source={backgroundImage}
        style={backgroundImageStyle}
        testID="pdp-summary-hero-background"
      />
      <View
        ref={contentRef}
        onLayout={handleContentLayout}
        style={styles.content}
        testID="pdp-summary-hero-content"
      >
        {children}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  content: {
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
